import * as TYPES from './types'
import * as API from '../api'
import { abortAllRequests } from '../api'
import { LIVEDATA_DISCONNECT } from '../liveData/types'
import { PROJECTS_RESET_DATA } from '../projects/types'
import { BillingTierDB } from '../admin/types'

const generateAuthStoragePayload = (authToken:string, gnUser:TYPES.GNUserPublic, systemSettings:TYPES.GNSystemSettings ) => {
  sessionStorage.setItem('authToken', authToken);
  sessionStorage.setItem('userID', gnUser.userID);
  sessionStorage.setItem('userName', gnUser.userName);
  sessionStorage.setItem('userEmail', gnUser.userEmail);
  sessionStorage.setItem('userRole', gnUser.userRole);
  sessionStorage.setItem('userPictureUrl', gnUser.userPictureUrl);
  sessionStorage.setItem('userCountry', gnUser.country);
  sessionStorage.setItem('userSinceTimestamp', gnUser.sinceTimestamp.toString());
  sessionStorage.setItem('isColaborationEmailSubscribed', gnUser.settings.isColaborationEmailSubscribed.toString());
  sessionStorage.setItem('stripePaymentMethod', JSON.stringify(gnUser.settings.stripePaymentMethod));

  const safeSystemSettings: TYPES.GNSystemSettings = {
    actualUEPluginVersion: systemSettings.actualUEPluginVersion || '',
    latestUEPluginVersionDownloadURL: systemSettings.latestUEPluginVersionDownloadURL || '',
    publicBillingTiers: systemSettings.publicBillingTiers || []
  };

  sessionStorage.setItem('actualUEPluginVersion', safeSystemSettings.actualUEPluginVersion);
  sessionStorage.setItem('latestUEPluginVersionDownloadURL', safeSystemSettings.latestUEPluginVersionDownloadURL);
  sessionStorage.setItem('publicBillingTiers', JSON.stringify(safeSystemSettings.publicBillingTiers));

  return {
    authToken,
    gnUser,
    systemSettings: safeSystemSettings,
  }
}

const updateAuthStorageUserPayload = (gnUser:TYPES.GNUserPublic) => {
  sessionStorage.setItem('userID', gnUser.userID);
  sessionStorage.setItem('userName', gnUser.userName);
  sessionStorage.setItem('userEmail', gnUser.userEmail);
  sessionStorage.setItem('userRole', gnUser.userRole);
  sessionStorage.setItem('userPictureUrl', gnUser.userPictureUrl);
  sessionStorage.setItem('userCountry', gnUser.country);
  sessionStorage.setItem('userSinceTimestamp', gnUser.sinceTimestamp.toString());
  sessionStorage.setItem('isColaborationEmailSubscribed', gnUser.settings.isColaborationEmailSubscribed.toString());
  sessionStorage.setItem('stripePaymentMethod', JSON.stringify(gnUser.settings.stripePaymentMethod));

  return {
    gnUser
  }
}

export function authSetError (error: string) {
    return (dispatch: any) => {
        dispatch({ type: TYPES.AUTH_ERROR, error });
    };
}

export function authClearError () {
    return (dispatch: any) => {
        dispatch({ type: TYPES.AUTH_CLEAR_ERROR });
    };
}

/**
 * Logout
 */
export function authLogout () {
    return async (dispatch: any, getState: any) => {
        // cancel all in-flight HTTP requests immediately
        abortAllRequests();

        // clean all possible timeouts (for liveData updates, etc)
        for (let i = 1; i < 99999; i++) {
            window.clearTimeout(i);
        }
        // clear session storage
        sessionStorage.clear();

        // Disconnect live socket if open (inline — avoids cross-slice import)
        const { liveData } = getState();
        if (liveData?.socket) {
            liveData.socket.close();
        }
        
        dispatch({ type: LIVEDATA_DISCONNECT }); // reset liveData slice to default state (avoid keeping old socket instance in memory)
        dispatch({ type: PROJECTS_RESET_DATA }); // reset projects data (avoid keeping old projects data in memory)
        dispatch({ type: TYPES.AUTH_LOGOUT }); // reset auth slice to default state
        return;
    }
}

/**
 * Login
 * @param {string} googleAuthToken
 */
export function authLogin (googleAuthToken: string) {
    return async (dispatch: any) => {
        // define request state as in progress
        dispatch({ type: TYPES.AUTH_IN_PROGRESS });
        
        function onSuccess (json: any) {
            if('data' in json && 'gnUser' in json.data) {
              dispatch({
                type: TYPES.AUTH_LOGIN,
                payload: generateAuthStoragePayload(
                  json.data.authToken, 
                  (json.data.gnUser as TYPES.GNUserPublic),
                  (json.data.systemSettings as TYPES.GNSystemSettings)
                )
              })
            } else {
              dispatch({ type: TYPES.AUTH_ERROR, error: `wrong API data format`  })
            }
            return;
        };

        function onError (error: any) {
            let errorCode = '';
            
            try {
                errorCode = error.error.code;
            } catch(e) { }

            dispatch({ type: TYPES.AUTH_ERROR, error: errorCode.length ? `${errorCode}` : `unknown API error`  })
            return;
        }

        try {
            const response = await API.httpRequest(API.URL.AUTH_LOGIN, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: googleAuthToken },
                body: null
            })

            const json = await response.json();

            if(response.status === 200) return onSuccess(json);
            else return onError(json);
        } catch (error) {
            return onError(error)
        }
    }
}

export function authRenewUserData() {
    return async (dispatch: any, getState: any) => {
        // get auth reducer from Redux
        const { auth } = getState();
        
        function onSuccess(json: any) {
            if('data' in json && 'gnUser' in json.data) {
                dispatch({
                    type: TYPES.AUTH_UPDATE_GNUSER,
                    payload: updateAuthStorageUserPayload((json.data.gnUser as TYPES.GNUserPublic))
                })
                return {};
            } 

            return { error: json.error?.description || json.error?.code || 'Failed to remove payment method.' };
        }

        function onError(json: any) {
            return { error: json.error?.description || json.error?.code || 'Failed to remove payment method.' };
        }
        try {
            const response = await API.httpRequest(API.URL.AUTH_GET_USER, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: auth.authToken },
                body: JSON.stringify({ userID: auth.gnUser.userID })
            })

            const json = await response.json();
            if(response.status === 200) return onSuccess(json);
            else return onError(json);
        } catch (error) {
            return onError(error);
        }
    }
}

/**
 * Delete User Account and all related data
 */
export function authDeleteAccount() {
    return async (dispatch:any, getState: any) => {
        // get auth reducer from Redux
        const { auth } = getState();

        await dispatch({ type: TYPES.AUTH_IN_PROGRESS});

        async function onSuccess(json: any) {
            return;
        }

        async function onError(error:any) {
            let errorCode = '';
            
            try {
                errorCode = error.error.description.length ? error.error.description : error.error.code;
            } catch(e) { 
                errorCode = `unknown error: ${JSON.stringify(e)}`;
            }

            await dispatch({ type: TYPES.AUTH_ERROR, error: errorCode.length ? `${errorCode}` : `unknown API error`  });
            return;
        }

        try {
            const response = await API.httpRequest(API.URL.AUTH_REMOVE_ACCOUNT, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: auth.authToken },
                body: JSON.stringify({ userID: auth.gnUser.userID })
            })

            const json = await response.json();
            switch(response.status){
                // case 403:{
                //     await dispatch(authLogout());
                //     return [];
                // }
                case 200:{
                    await onSuccess(json);
                    return;
                }
                default:{
                    await onError(json);
                    return json;
                }
            }
        } catch (error) {
            onError(error);
            return error;
        }
    }
}

export function authUpdateSettings (settings: Partial<TYPES.GNUserPublic['settings']>) {
    return async (dispatch: any, getState: any) => {
        // get auth reducer from Redux
        const { auth } = getState();

        await dispatch({ type: TYPES.AUTH_UPDATE_IN_PROGRESS });

        async function onSuccess(json: any) {
            if('data' in json && 'gnUser' in json.data) {
                dispatch({
                    type: TYPES.AUTH_LOGIN,
                    payload: generateAuthStoragePayload(
                        auth.authToken, 
                        (json.data.gnUser as TYPES.GNUserPublic),
                        (json.data.systemSettings as TYPES.GNSystemSettings)
                    )
                });
            } else {
                dispatch({ type: TYPES.AUTH_ERROR, error: `wrong API data format`  })
            }    
            return;
        }

        async function onError(error: any) {
            let errorCode = '';

            try {
                errorCode = error.error.description.length ? error.error.description : error.error.code;
            } catch (e) {
                errorCode = `unknown error: ${JSON.stringify(e)}`;
            }

            await dispatch({ type: TYPES.AUTH_ERROR, error: errorCode.length ? `${errorCode}` : `unknown API error` });
            return;
        }

        try {
            const response = await API.httpRequest(API.URL.AUTH_UPDATE_SETTINGS, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: auth.authToken },
                body: JSON.stringify(
                    { 
                        userID: auth.gnUser.userID, 
                        isColaborationEmailSubscribed: settings.isColaborationEmailSubscribed 
                    }
                )
            })

            const json = await response.json();
            switch (response.status) {
                case 200: {
                    await onSuccess(json);
                    return;
                }
                default: {
                    await onError(json);
                    return json;
                }
            }
        } catch (error) {
            onError(error);
            return error;
        }
    }
}

export function authUpdateToken(authToken: string) {
    return async (dispatch: any, getState: any) => {
        await dispatch({ type: TYPES.AUTH_UPDATE_TOKEN, authToken });
        
        // const { auth } = getState();
        // console.log(auth);
        return;
    }
}

/* Billign Tiers */
export function authUpdateBillingTiersInAuthStorage(publicBillingTiers: BillingTierDB[]) {
    return async (dispatch: any, getState: any) => {
        sessionStorage.setItem('publicBillingTiers', JSON.stringify(publicBillingTiers));
        await dispatch({ type: TYPES.AUTH_UPDATE_BILLING_TIERS, publicBillingTiers });
        return;
    }
}

/* Billing Stripe */
export function authUserBillingGetStripeClientSecret() {
    return async (dispatch: any, getState: any) => {
        // get auth reducer from Redux
        const { auth } = getState();
        
        function onSuccess(json: any) {
            if('data' in json && 'stripeClientSecret' in json.data) {
                return { stripeClientSecret: json.data.stripeClientSecret };
            } 

            return { error: json.error?.description || json.error?.code || 'Failed to initialize payment setup.' };
        }

        function onError(json: any) {
            return { error: json.error?.description || json.error?.code || 'Failed to initialize payment setup.' };
        }
        try {
            const response = await API.httpRequest(API.URL.AUTH_BILLING_SETUP_INTENT, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: auth.authToken },
                body: JSON.stringify({ userID: auth.gnUser.userID })
            })

            const json = await response.json();
            if(response.status === 200) return onSuccess(json);
            else return onError(json);
        } catch (error) {
            return onError(error);
        }
    }
}

export function authUserBillingRemovePaymentMethod() {
    return async (dispatch: any, getState: any) => {
        // get auth reducer from Redux
        const { auth } = getState();
        
        function onSuccess(json: any) {
            if('data' in json && 'gnUser' in json.data) {
                dispatch({
                    type: TYPES.AUTH_UPDATE_GNUSER,
                    payload: updateAuthStorageUserPayload((json.data.gnUser as TYPES.GNUserPublic))
                })
                return {};
            } 

            return { error: json.error?.description || json.error?.code || 'Failed to remove payment method.' };
        }

        function onError(json: any) {
            return { error: json.error?.description || json.error?.code || 'Failed to remove payment method.' };
        }
        try {
            const response = await API.httpRequest(API.URL.AUTH_BILLING_REMOVE_PAYMENT_METHOD, {
                method: 'post',
                headers: { 'Content-Type': 'application/json', Authorization: auth.authToken },
                body: JSON.stringify({ userID: auth.gnUser.userID })
            })

            const json = await response.json();
            if(response.status === 200) return onSuccess(json);
            else return onError(json);
        } catch (error) {
            return onError(error);
        }
    }
}